
import React, { useRef } from 'react';
import { cn } from "@/lib/utils";
import { useIsMobile } from '@/hooks/use-mobile';
import { LayoutMode } from '@/hooks/usePortfolioState';
import { usePortfolioSectionSticky } from '@/hooks/portfolio/usePortfolioSectionSticky';
import { usePortfolioSectionState } from '@/hooks/portfolio/usePortfolioSectionState';
import PortfolioSectionHeader from '@/components/portfolio/PortfolioSectionHeader';
import PortfolioSectionContent from '@/components/portfolio/PortfolioSectionContent';
import { Business } from '@/schemas/business'; // Changed import path
import { InfoIcon } from 'lucide-react';

interface PortfolioSectionProps {
  title: string;
  portfolioRole?: string | null;
  businesses: Business[];
  className?: string;
  isFirst?: boolean;
  emptyState?: React.ReactNode;
  portfolioId?: string;
  layoutMode?: LayoutMode;
  showFilterOptions?: boolean;
  showMobileSearch?: boolean;
}

const PortfolioSection: React.FC<PortfolioSectionProps> = ({ 
  title, 
  portfolioRole,
  businesses,
  className,
  isFirst = false,
  emptyState,
  portfolioId = 'default-portfolio',
  layoutMode = 'list',
  showFilterOptions = false,
  showMobileSearch = false
}) => {
  // For the "No Portfolio" section, always expand and don't persist state
  const { isExpanded, toggleExpanded } = usePortfolioSectionState({
    defaultExpanded: true,
    portfolioId,
    persistState: portfolioId !== "no-portfolio" // Don't persist state for No Portfolio section
  });
  
  const sectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  const isGridView = !isMobile && layoutMode === 'grid';

  const { isSticky, stickyTopPosition } = usePortfolioSectionSticky({
    sectionRef,
    headerRef,
    contentRef,
    isMobile,
    showFilterOptions,
    showMobileSearch
  });

  return (
    <div 
      className={cn(
        isMobile ? "mb-0" : "mb-4", // Reduced margin and removed separator
        "relative",
        // Add special styling for No Portfolio section
        portfolioId === "no-portfolio" && "bg-gray-50 rounded-lg p-4 border border-gray-200",
        className
      )}
      ref={sectionRef}
    >
      <div 
        ref={headerRef}
        className={cn(
          "transition-all duration-200",
          isSticky && "sticky-header-container"
        )}
        style={isSticky ? { 
          position: 'sticky', 
          top: `${stickyTopPosition}px`,
          backgroundColor: 'var(--brand-light, #F9FAFB)',
          transition: 'top 0.2s ease-in-out, background-color 0.2s ease-in-out',
          zIndex: 20,
          width: '100%',
          paddingBottom: 0
        } : undefined}
      >
        <PortfolioSectionHeader
          title={title}
          portfolioRole={portfolioRole}
          isExpanded={isExpanded}
          isSticky={isSticky}
          isMobile={isMobile}
          portfolioId={portfolioId}
          toggleAccordion={toggleExpanded}
        />
      </div>
      
      <div 
        ref={contentRef} 
        className="mt-3"
      >
        <PortfolioSectionContent
          isExpanded={isExpanded}
          businesses={businesses}
          isGridView={isGridView}
          emptyState={emptyState}
        />
      </div>
    </div>
  );
};

export default PortfolioSection;
